import './LoaderEdit.scss';

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Helmet } from "react-helmet";
import { Breadcrumb, Button, Card, Col, Container, Form } from 'react-bootstrap'
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { LlmConfig, MetaLoaderListDTO, RagLoader } from '../../../models/LLM';
import { Datasource, ObjectStorageDatasource } from '../../../models/Datasource';
import { RootState } from '../../../store';
import { sectionLabelsSelector } from '../../../slices/sectionsSlice';
import { useToasts } from '../../../contexts/toasts';
import { Llm } from '../../../api/Llm';
import { Datasources } from '../../../api/Datasources';
import { cloneDeep, set } from 'lodash';
import { accessLevelOptions, fixDirName, normalizePath } from '../../../utils';
import { Alert, Flex, Select, Tag } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import AppLoader from '../../../components/common/AppLoader';
import PathExplorer from '../../../components/generics/PathExplorer';
import LoaderClassDropdown from '../../../components/generics/LoaderClassDropdown';
import { useFullScreenLoader } from '../../../contexts/fullScreenLoader';
import AlidaLoader from '../../../components/common/AlidaLoader';
import IconButton from '../../../components/common/IconButton';
import UploadModal from '../../../components/generics/UploadModal';

export default function LoaderEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [error, setError] = useState(false);
  const [metaLoaders, setMetaLoaders] = useState<MetaLoaderListDTO[]>();
  const [validated, setValidated] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingLoader, setEditingLoader] = useState<RagLoader>();
  const [datasources, setDatasources] = useState<Datasource[]>();
  const [selectedDatasource, setSelectedDatasource] = useState<ObjectStorageDatasource>();
  const selectedMetaLoader = useMemo(() => metaLoaders?.find(m => m.name === editingLoader?.type), [metaLoaders, editingLoader]);

  const loaderLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "loaders"));

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const getLoader = useCallback(() => {
    Llm.getLoader(id!)
      .then(
        ({ data }) => setEditingLoader(data),
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('dataset-error', message, `${loaderLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [id, showToast, loaderLabels]);

  useEffect(() => {
    if (id) getLoader();
    else setEditingLoader(new RagLoader());
  }, [id, getLoader]);

  useEffect(() => {
    if (editingLoader && datasources && !selectedDatasource) {
      const found = editingLoader.config?.findIndex(c => c.type === 'datasource.id');
      if (found !== undefined && found > -1) {
        const datasource = datasources.find(d => d.id === editingLoader.config![found].value);
        setSelectedDatasource(datasource as ObjectStorageDatasource);
      }
    }
  }, [datasources, editingLoader, selectedDatasource]);

  const initMetaLoaders = useCallback(() => {
    Llm.getLoadersDocumentation()
      .then(
        ({ data }) => setMetaLoaders(data),
        (err) => {
          setMetaLoaders([]);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('meta-loaders-error', message, `${loaderLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
    // eslint-disable-next-line
  }, [showToast]);

  const initDatasources = useCallback(() => {
    const params = new URLSearchParams([['type', 'objectStorage']]);

    Datasources.getDatasources(params)
      .then(
        ({ data }) => setDatasources(data),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('datasources-error', message, `Datasources Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showToast]);

  useEffect(() => {
    initMetaLoaders();
    initDatasources();
  }, [initMetaLoaders, initDatasources]);

  const getLoaderDocumentation = useCallback(() => {
    Llm.getLoaderDocumentation(selectedMetaLoader!.name)
      .then(
        ({ data }) => {
          const config = data.config.map(conf => {
            if (conf.required && conf.default) conf.value = conf.default;
            return conf;
          });
          setEditingLoader(l => ({ ...l!, config }));
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('meta-loader-error', message, `${loaderLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }, [selectedMetaLoader, showToast, loaderLabels]);

  useEffect(() => {
    if (!editingLoader?.config && editingLoader?.type && selectedMetaLoader) getLoaderDocumentation();
  }, [editingLoader, selectedMetaLoader, getLoaderDocumentation]);

  const cancelEdit = useCallback(() => navigate('/llm/loaders'), [navigate]);

  const submitLoader = useCallback(() => {
    showLoader();

    const body: RagLoader = {
      ...editingLoader!,
      config: editingLoader!.config!.map(c => {
        if (c.required && c.value === undefined) c.value = c.default;
        if (c.type === 'datasource.path') c.value = typeof c.value === 'object' ? (c.value as string[]).join('/') : c.value;
        return c;
      }),
    }

    const promise = id !== undefined
      ? Llm.editLoader(body)
      : Llm.createLoader(body);

    promise.then(
      () => {
        hideLoader();
        showToast('loader-success', `${loaderLabels.singular} ${id !== undefined ? 'Updated' : 'Created'} successfully`, `${loaderLabels.singular} Success`, 'success');
        cancelEdit();
      },
      (err) => {
        hideLoader();
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('loaders-error', message, `${loaderLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
      });
  }, [showLoader, hideLoader, showToast, loaderLabels, cancelEdit, editingLoader, id]);

  const handleSubmit = useCallback((evt: React.FormEvent<HTMLFormElement>) => {
    setValidated(true);
    evt.stopPropagation();
    evt.preventDefault();
    const form = evt.currentTarget;

    if (
      form.checkValidity() === true && editingLoader!.accessLevel &&
      editingLoader!.type && editingLoader!.config
    ) submitLoader();
  }, [editingLoader, submitLoader]);

  const handleClassLoaderChange = useCallback((type: string) => setEditingLoader(l => ({ ...l!, type, config: undefined })), []);
  const openUploadModal = useCallback(() => setShowModal(true), []);
  const handleModalClose = useCallback(() => setShowModal(false), []);

  const handleFieldChange = useCallback((path: string, pure?: boolean, checkbox?: boolean, datasource?: boolean) => (evt: any) => {
    const value = checkbox
      ? evt.target?.checked
      : pure ? evt : evt.target?.value;

    setEditingLoader(curr => {
      const newLoader = cloneDeep(curr!);
      if (datasource) {
        const found = newLoader.config?.findIndex(prop => prop.type === 'datasource.path')!;
        if (found > -1) {
          const foundDatasource = datasources?.find(d => d.id === value) as ObjectStorageDatasource;
          const newConfigs = cloneDeep(newLoader.config)!;
          newConfigs[found].value = foundDatasource?.prefixPath ? fixDirName(foundDatasource.prefixPath) : "";
          newLoader.config = newConfigs;
        }
      }
      set(newLoader, path, value);
      return newLoader;
    });

    // duplicated find but avoiding side-effect of having
    // a setState inside of another setState action
    if (datasource) {
      const foundDatasource = datasources?.find(d => d.id === value) as ObjectStorageDatasource;
      setSelectedDatasource(foundDatasource);
    }
  }, [datasources]);

  const renderDatasourceSelect = useCallback((config: LlmConfig, index: number) => {
    return (
      <Form.Group className="mb-3" controlId="loader-datasource" key={index}>
        <Form.Label className="mb-0">Datasource</Form.Label>
        <Select
          loading={!datasources}
          virtual={true}
          options={datasources?.map(d => ({
            label: (
              <Flex align="center">
                <Col>
                  <h6 className="mb-1">{d.name}</h6>
                  <Flex align="center">
                    <Tag color="blue">{d.accessLevel}</Tag>
                    <Tag icon={<FontAwesomeIcon icon={solid('user')} size="sm" className="me-1" />}>{d.owner?.name}</Tag>
                  </Flex>
                </Col>
                <Tag className="rounded-pill" color="magenta">{d.driverType}</Tag>
                {/* <span className="line-clamp-2 text-muted">{d.description}</span> */}
              </Flex>
            ),
            value: d.id,
          }))}
          className="w-100 h-auto"
          showSearch
          value={config.value as any}
          onChange={handleFieldChange(`config.[${index}].value`, true, false, true)}
          placeholder={`Select ${loaderLabels.singular} datasource...`}
          allowClear
        />
        <Form.Text>{config.description}</Form.Text>
      </Form.Group>
    )
  }, [datasources, loaderLabels, handleFieldChange]);

  const renderPathExplorer = useCallback((prop: LlmConfig, index: number) => {
    return (
      <>
        <Form.Group className="mb-3" controlId="loader-path" key={index}>
          <Form.Label className="mb-0 d-block">Path</Form.Label>
          <Form.Text>{prop.description}</Form.Text>
          <PathExplorer
            datasource={selectedDatasource}
            path={prop.value as string}
            onPathChange={handleFieldChange(`config.[${index}].value`, true)}
            searchCol={4}
            pathActions={
              <IconButton type="button" tooltip="Upload files" onClick={openUploadModal} className="p-1" disabled={!selectedDatasource}>
                <FontAwesomeIcon icon={solid('upload')} size="sm" />
              </IconButton>
            }
          />
        </Form.Group>

        <UploadModal show={showModal} onClose={handleModalClose} selectedDatasource={selectedDatasource} selectedPath={normalizePath(prop.value as string || "")} />
      </>
    )
  }, [selectedDatasource, handleFieldChange, handleModalClose, openUploadModal, showModal]);

  const renderMetaLoaderDetail = useCallback(() => {
    if (!editingLoader!.config && editingLoader!.type) return <AppLoader className="py-5" justify="center" size={100} />

    return editingLoader!.config?.map((prop, index) => {
      if (prop.type === 'datasource.id') return renderDatasourceSelect(prop, index);
      if (prop.type === 'datasource.path') return renderPathExplorer(prop, index);

      return (
        <Form.Group className="mb-3" key={index}>
          {prop.type === 'boolean'
            ? (
              <Flex align="center">
                <Col>
                  <Form.Label className="d-block">{prop.name} {prop.required && '*'}</Form.Label>
                  <Form.Text>{prop.description}</Form.Text>
                </Col>
                <Form.Check className="form-check-lg m-0" checked={prop.value as boolean} onChange={handleFieldChange(`config.[${index}].value`, false, true)} />
              </Flex>
            )
            : (
              <>
                <Form.Label>{prop.name} {prop.required && '*'}</Form.Label>
                {prop.type === 'array' || prop.type === 'select'
                  ? (
                    <Select
                      className="w-100"
                      mode={prop.type === 'array' ? "tags" : undefined}
                      options={prop.options}
                      placeholder={prop.default ? `${prop.default}` : prop.type} value={prop.value as string[] || []}
                      onChange={handleFieldChange(`config.[${index}].value`, true)}
                      status={validated && prop.required && (!prop.value || (prop.type === 'array' && (prop.value as string[]).length === 0)) ? 'error' : undefined}
                    />
                  )
                  : (
                    <Form.Control
                      size="sm"
                      placeholder={prop.default ? `${prop.default}` : prop.type}
                      value={prop.value as string || ""}
                      onChange={handleFieldChange(`config.[${index}].value`)}
                      required={prop.required}
                    />
                  )
                }
                {validated && prop.required && prop.type !== 'boolean' && (!prop.value || (prop.type === 'array' && (prop.value as string[]).length === 0)) && <Form.Control.Feedback className="d-block" type="invalid">{prop.name} is required.</Form.Control.Feedback>}
                <Form.Text>{prop.description}</Form.Text>
              </>
            )
          }
        </Form.Group>
      )
    })
  }, [validated, editingLoader, handleFieldChange, renderDatasourceSelect, renderPathExplorer]);

  const renderForm = useCallback(() => {
    return editingLoader && (
      <Card>
        <Card.Body>
          <Form validated={validated} onSubmit={handleSubmit} noValidate>
            <Form.Group className="mb-3" controlId="loader-name">
              <Form.Label>Name</Form.Label>
              <Form.Control placeholder={`${loaderLabels.singular} Name`} size="sm" value={editingLoader.name} onChange={handleFieldChange('name')} required />
              <Form.Control.Feedback type="invalid">{loaderLabels.singular} name is required.</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="loader-description">
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows={3} name="description" placeholder={`${loaderLabels.singular} Description`} size="sm" value={editingLoader.description} onChange={handleFieldChange('description')} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Privacy Level</Form.Label>
              <Select
                placeholder="Select privacy level"
                value={editingLoader.accessLevel}
                allowClear
                onChange={handleFieldChange('accessLevel', true)}
                className="w-100"
              >
                {accessLevelOptions.map((option, index) => (
                  <Select.Option key={index} value={option.value} label={option.label}>
                    <FontAwesomeIcon icon={option.icon} fixedWidth />
                    <span className="ms-2">{option.label}</span>
                  </Select.Option>
                ))}
              </Select>
              {validated && !editingLoader.accessLevel && <Form.Control.Feedback className="d-block" type="invalid">Privacy Level is required.</Form.Control.Feedback>}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Tags</Form.Label>
              <Select mode="tags" className="w-100" placeholder={`${loaderLabels.singular} Tags`} value={editingLoader?.tags} onChange={handleFieldChange('tags', true)} />
            </Form.Group>

            <Form.Group className="mb-3" controlId="loader-class">
              <Form.Label className="mb-0">{loaderLabels.singular} Class</Form.Label>
              <LoaderClassDropdown
                value={editingLoader.type}
                loaders={metaLoaders}
                onChange={handleClassLoaderChange}
              />
              {validated && !editingLoader.type && <Form.Control.Feedback className="d-block" type="invalid">{loaderLabels.singular} Class is required.</Form.Control.Feedback>}
            </Form.Group>

            {renderMetaLoaderDetail()}

            <div className="d-flex justify-content-between mt-3">
              <Button type="button" variant="light" className="border" size="sm" onClick={cancelEdit}>Cancel</Button>
              <Button type="submit" variant="success" size="sm">
                <FontAwesomeIcon icon={solid('save')} />
                <span className="ms-2">Save</span>
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    )
  }, [editingLoader, renderMetaLoaderDetail, loaderLabels, cancelEdit, handleFieldChange, handleClassLoaderChange, handleSubmit, metaLoaders, validated]);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert
        type="error"
        showIcon
        message={`An error occurred while retrieving ${loaderLabels.singular}. Try again later.`}
      />
    )

    return editingLoader
      ? renderForm()
      : (
        <div className="p-5 text-center">
          <AlidaLoader />
        </div>
      );
  }, [error, editingLoader, loaderLabels, renderForm]);

  return (
    <Container className="loader-edit my-3">
      <Helmet>
        <title>{loaderLabels.singular} {id ? `${id} Edit` : "Creation"}</title>
      </Helmet>

      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/llm/loaders' }}>{loaderLabels.plural}</Breadcrumb.Item>
        {id
          ? <Breadcrumb.Item active>{editingLoader?.name} Edit</Breadcrumb.Item>
          : <Breadcrumb.Item active>New {loaderLabels.singular}</Breadcrumb.Item>
        }
      </Breadcrumb>

      {renderAsyncContent()}
    </Container>
  )
}